import { useCallback } from 'react'
import { PageHead } from '@components/PageHead'
import { deleteSendById } from '@features/send/services/sendFlow'
import { useSendList, type SendFilter } from '@features/send/hooks/useSendList'
import { type SendItem } from '@features/send/types'
import { copyToClipboard, getEffectiveClipboardTtlSec } from '@services/clipboard'
import { AsyncButton } from '@shared/ui/AsyncButton'
import { EmptyState } from '@shared/ui/EmptyState'
import { Notice } from '@shared/ui/Notice'
import { useToast } from '@shared/ui/useToast'
import { Icon } from '@/icons/Icon'
import { useTranslation } from '@/i18n'

/**
 * Send 一覧画面 (T-D-Send、design/js/screens-send.jsx を移植)。
 *
 * tabs: all / text / file / expired
 * 行アクション: リンクコピー / リンクを開く / 削除
 * ゼロ知識: receiveUrl はクライアント側で組み立て済み (sendFlow.ts)。
 */

const FILTERS: readonly { key: SendFilter; i18nKey: string }[] = [
  { key: 'all', i18nKey: 'send.list.tab_all' },
  { key: 'text', i18nKey: 'send.list.tab_text' },
  { key: 'file', i18nKey: 'send.list.tab_file' },
  { key: 'expired', i18nKey: 'send.list.tab_expired' },
] as const

const formatExpiry = (expiresAt: string | null, noExpiryLabel: string): string => {
  if (expiresAt === null) return noExpiryLabel

  return new Date(expiresAt).toLocaleDateString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
}

export const SendListPage = (): JSX.Element => {
  const { t } = useTranslation()
  const { show: showToast } = useToast()
  const { state, filter, setFilter, filtered, counts, reload } = useSendList()

  const onCopyLink = useCallback(
    async (item: SendItem): Promise<void> => {
      try {
        await copyToClipboard(item.receiveUrl)
        showToast({
          titleKey: 'send.toast.link_copied',
          titleVars: { ttl: getEffectiveClipboardTtlSec() },
          tone: 'success',
          dedupe: true,
        })
      } catch {
        showToast({ titleKey: 'send.toast.copy_failed', tone: 'danger', dedupe: true })
      }
    },
    [showToast],
  )

  const onDelete = useCallback(
    async (item: SendItem): Promise<void> => {
      try {
        await deleteSendById(item.id)
        showToast({ titleKey: 'send.toast.deleted', tone: 'success', dedupe: true })
        void reload()
      } catch {
        showToast({ titleKey: 'send.toast.copy_failed', tone: 'danger', dedupe: true })
      }
    },
    [showToast, reload],
  )

  const totalCount = state.kind === 'ready' ? state.items.length : 0
  const failureCount = state.kind === 'ready' ? state.failureCount : 0

  return (
    <div>
      <PageHead
        title={t('send.list.title')}
        sub={t('send.list.subtitle', { count: totalCount })}
        actions={
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              window.location.hash = 'send/new'
            }}
          >
            <Icon name="plus" aria-hidden="true" />
            {t('send.list.new_button')}
          </button>
        }
        tabs={FILTERS.map(f => ({ key: f.key, label: t(f.i18nKey), n: counts[f.key] }))}
        activeTab={filter}
        onTab={k => setFilter(k as SendFilter)}
      />

      <div className="page-body flush">
        {failureCount > 0 && (
          <div className="send-warning-bar">
            <Notice tone="warn" ico="alert">
              {t('send.list.decryption_warning', { count: failureCount })}
            </Notice>
          </div>
        )}

        {state.kind === 'error' && (
          <div className="send-state-message">
            <Notice tone="danger" ico="alert">
              {t(state.messageKey)}
            </Notice>
            <button type="button" className="btn btn-secondary" onClick={() => void reload()}>
              {t('send.list.tab_all')}
            </button>
          </div>
        )}

        {state.kind === 'ready' && filtered.length === 0 && (
          <EmptyState
            icon="send"
            title={t('send.list.empty.title')}
            sub={t('send.list.empty.subtitle')}
            action={
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  window.location.hash = 'send/new'
                }}
              >
                <Icon name="plus" aria-hidden="true" />
                {t('send.list.empty.cta')}
              </button>
            }
          />
        )}

        {state.kind === 'ready' && filtered.length > 0 && (
          <ul className="list send-list" role="list">
            <li className="list-head send-list-head" role="presentation" aria-hidden="true">
              <div />
              <div>{t('send.list.col_name')}</div>
              <div>{t('send.list.col_type')}</div>
              <div>{t('send.list.col_access')}</div>
              <div>{t('send.list.col_expires')}</div>
              <div />
            </li>
            {filtered.map(item => (
              <li key={item.id} className="list-row send-list-row" role="listitem">
                <div className="send-row-icon" aria-hidden="true">
                  <Icon name={item.payload.type === 'file' ? 'file-text' : 'send'} />
                </div>
                <div className="send-row-main">
                  <div className="nm">{item.payload.name}</div>
                  <div className="sb mono send-row-url">{item.receiveUrl}</div>
                </div>
                <div className="sb">
                  {item.payload.type === 'file'
                    ? t('send.list.row.type_file')
                    : t('send.list.row.type_text')}
                </div>
                <div className="sb mono">
                  {item.accessCount} / {item.maxAccess}
                </div>
                <div className="sb mono send-row-expires">
                  {formatExpiry(item.expiresAt, t('send.list.row.no_expiry'))}
                </div>
                <div className="acts">
                  <button
                    type="button"
                    className="icon-btn sm"
                    title={t('send.list.row.copy_link')}
                    aria-label={t('send.list.row.copy_link')}
                    onClick={() => void onCopyLink(item)}
                  >
                    <Icon name="copy" />
                  </button>
                  <button
                    type="button"
                    className="icon-btn sm"
                    title={t('send.list.row.open_link')}
                    aria-label={t('send.list.row.open_link')}
                    onClick={() => {
                      window.open(item.receiveUrl, '_blank', 'noopener')
                    }}
                  >
                    <Icon name="share" />
                  </button>
                  <AsyncButton
                    className="icon-btn sm"
                    title={t('send.list.row.delete')}
                    aria-label={t('send.list.row.delete')}
                    onClick={() => onDelete(item)}
                  >
                    <Icon name="trash" />
                  </AsyncButton>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
